import * as React from "react";
import { Formik } from "formik";
import { CreateMediaProjectForm, FieldName, FormValues } from "./CreateMediaProjectForm/CreateMediaProjectForm";
import { useQuery } from "react-query";
import {
  DubbingLanguageAndVoice,
  JobRequestFileCategory,
  JobRequestNewFile,
  LltsLanguage,
  MachineTranslationService,
  UtilsService,
  Xl8DubbingVoice,
  Xl8SubtitleType
} from "gen/clients/llts";
import { SelectOption } from "@mui/base";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";
import { useUserSession } from "hooks/useUserSession";
import { useS3FileUpload } from "hooks/useS3FileUpload/useS3FileUpload2";
import { AlertDialog } from "components/AlertDialog/AlertDialog";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { LinearProgressWithLabel } from "components/LinearProgressWithLabel/LinearProgressWithLabel";
import { useTranslation } from "react-i18next";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { createProjectName } from "utils/projectUtils";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { MediaProjectType } from "../SelectMediaProjectTypeStep/SelectProjectTypeStep";

interface Props {
  mediaProjectType: MediaProjectType;
  onClose: () => void;
  onSuccess: () => void;
}

const MAX_TARGET_LANGUAGES = 10;
const MAX_TOTAL_FILES_SIZE_BYTES = 250 * 1024 * 1024; // 250 MB
const DEFAULT_MAX_AUDIO_FILE_SIZE_MB = 1;
const DEFAULT_MAX_VIDEO_FILE_SIZE_MB = 1;

const CreateMediaProjectStep: React.FC<Props> = ({ mediaProjectType, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { username } = useUserSession();
  const [selectedSourceLanguageId, setSelectedSourceLanguageId] = React.useState<string>();
  const [selectedTargetLanguageIds, setSelectedTargetLanguageIds] = React.useState<string[]>([]);

  const {
    invoke: uploadFiles,
    isUploading: areFilesUploading,
    error: filesUploadError,
    uploadProgress,
    reset: resetS3FileUpload
  } = useS3FileUpload();

  const {
    data: mtSettings,
    isLoading: areMtSettingsLoading,
    error: mtSettingsError
  } = useQuery(["getMtSettings"], {
    queryFn: MachineTranslationService.getMachineTranslationSettings
  });
  const {
    data: lltsLanguages,
    isLoading: areLltsLanguagesLoading,
    error: lltsLanguagesError
  } = useQuery(["listLltsLanguages", "mediaAi"], {
    queryFn: () => UtilsService.listLltsLanguages({ xl8SyncIdExists: true, xl8FileTranslationIdExists: true })
  });

  const {
    data: xl8TransLanguageCombinations,
    isLoading: areXl8TransLangCombinationsLoading,
    error: xl8TransLanguageCombinationsError
  } = useQuery(["getXl8FileTranslationLanguageCombinations"], {
    queryFn: () => UtilsService.getXl8FileTranslationLanguageCombinations({})
  });

  const {
    data: xl8DubbingVoices,
    isLoading: areXl8DubbingVoicesLoading,
    error: xl8DubbingVoicesError
  } = useQuery(["getXl8DubbingVoices"], {
    queryFn: UtilsService.getXl8DubbingVoices,
    enabled: mediaProjectType === MediaProjectType.DUBBING
  });

  const isLoading = areLltsLanguagesLoading || areXl8TransLangCombinationsLoading || areMtSettingsLoading;
  const error = lltsLanguagesError || xl8TransLanguageCombinationsError || xl8DubbingVoicesError || mtSettingsError;

  const { lltsLanguageById, lltsLanguageByXl8FileTranslationId } = React.useMemo(() => {
    const lltsLanguageById: Record<string, LltsLanguage> = {};
    const lltsLanguageByXl8FileTranslationId: Record<string, LltsLanguage> = {};
    (lltsLanguages || []).forEach(lltsLang => {
      lltsLanguageById[lltsLang.id] = lltsLang;
      if (lltsLang.xl8FileTranslationId) {
        lltsLanguageByXl8FileTranslationId[lltsLang.xl8FileTranslationId] = lltsLang;
      }
    });
    return { lltsLanguageById, lltsLanguageByXl8FileTranslationId };
  }, [lltsLanguages]);

  const sourceLanguageOptions: SelectOption<string>[] = React.useMemo(() => {
    if (!Object.keys(lltsLanguageByXl8FileTranslationId).length || !xl8TransLanguageCombinations) {
      return [];
    }
    return Object.keys(xl8TransLanguageCombinations)
      .map(sourceLanguageXl8TransId => lltsLanguageByXl8FileTranslationId[sourceLanguageXl8TransId])
      .filter(l => l !== undefined)
      .map(l => ({ label: l.name, value: l.id }))
      .sort(SELECT_OPTION_COMPARATOR);
  }, [lltsLanguageByXl8FileTranslationId, xl8TransLanguageCombinations]);

  const targetLanguageOptions: SelectOption<string>[] = React.useMemo(() => {
    if (!selectedSourceLanguageId) {
      return [];
    }
    const sourceLanguageOption: SelectOption<string> = {
      label: lltsLanguageById[selectedSourceLanguageId].name,
      value: selectedSourceLanguageId
    };
    const sourceLanguageXl8Id = lltsLanguageById[selectedSourceLanguageId].xl8FileTranslationId;
    if (!sourceLanguageXl8Id) {
      return [sourceLanguageOption];
    }
    return [sourceLanguageOption]
      .concat(
        (xl8TransLanguageCombinations || {})[sourceLanguageXl8Id]
          .map(xl8LangId => lltsLanguageByXl8FileTranslationId[xl8LangId])
          .filter(l => l !== undefined)
          .map(l => ({
            label: l.name,
            value: l.id
          }))
      )
      .sort(SELECT_OPTION_COMPARATOR);
  }, [lltsLanguageById, lltsLanguageByXl8FileTranslationId, selectedSourceLanguageId, xl8TransLanguageCombinations]);

  const dubbingVoicesByTargetLanguage: Map<LltsLanguage, Xl8DubbingVoice[]> = React.useMemo(() => {
    const result = new Map<LltsLanguage, Xl8DubbingVoice[]>();
    if (!selectedTargetLanguageIds.length || !xl8DubbingVoices) {
      return result;
    }
    selectedTargetLanguageIds.forEach(langId => {
      const lltsLanguage = lltsLanguageById[langId];
      const xl8DubLangCode = lltsLanguage.xl8DubbingId;
      if (xl8DubLangCode && langId !== selectedSourceLanguageId) {
        const voices = xl8DubbingVoices[xl8DubLangCode];
        result.set(lltsLanguage, voices);
      }
    });
    return result;
  }, [lltsLanguageById, selectedSourceLanguageId, selectedTargetLanguageIds, xl8DubbingVoices]);

  const initialValues = React.useMemo(() => {
    const initVals: FormValues = {
      [FieldName.projectName]: createProjectName(username),
      [FieldName.files]: [],
      [FieldName.sourceLanguage]: null,
      [FieldName.targetLanguages]: [],
      [FieldName.subtitlesType]: [Xl8SubtitleType.SRT]
    };
    lltsLanguages?.forEach(l => (initVals[`${FieldName.dubbingVoicePrefix}#${l.id}`] = ""));
    return initVals;
  }, [lltsLanguages, username]);

  const validate = React.useCallback(
    (values: FormValues) => {
      const errors: Record<FieldName, string> = {} as Record<FieldName, string>;
      const sourceLanguageId = values[FieldName.sourceLanguage]?.value;
      const targetLanguageIds = values[FieldName.targetLanguages]?.map(o => o.value);
      if (targetLanguageIds && targetLanguageIds.length > MAX_TARGET_LANGUAGES) {
        errors[FieldName.targetLanguages] = t("machineTranslation.mediaTranslation.validation.maxTargetLanguages");
      }
      const files = values[FieldName.files];
      const totalFilesSize = files.reduce((prev, currentFile) => prev + currentFile.size, 0);
      if (totalFilesSize >= MAX_TOTAL_FILES_SIZE_BYTES) {
        errors[FieldName.files] = t("machineTranslation.mediaTranslation.validation.maxTotalFilesSize");
      }

      files.forEach(file => {
        if (
          file.type.startsWith("audio/") &&
          file.size > (mtSettings?.maxAudioFileSizeMB || DEFAULT_MAX_AUDIO_FILE_SIZE_MB) * 1024 * 1024
        ) {
          errors[FieldName.files] = t("machineTranslation.mediaTranslation.validation.maxAudioFileSize", {
            filename: file.name,
            maxFileSizeMB: mtSettings?.maxAudioFileSizeMB || DEFAULT_MAX_AUDIO_FILE_SIZE_MB
          });
        }

        if (
          file.type.startsWith("video/") &&
          file.size > (mtSettings?.maxVideoFileSizeMB || DEFAULT_MAX_VIDEO_FILE_SIZE_MB) * 1024 * 1024
        ) {
          errors[FieldName.files] = t("machineTranslation.mediaTranslation.validation.maxVideoFileSize", {
            filename: file.name,
            maxFileSizeMB: mtSettings?.maxVideoFileSizeMB || DEFAULT_MAX_VIDEO_FILE_SIZE_MB
          });
        }
      });

      if (mediaProjectType === MediaProjectType.EMBEDDING_TRANSLATED_SUBTITLES_INTO_SOURCE_VIDEO) {
        const areAllFilesVideoFiles = files.every(file => file.type.startsWith("video/"));
        if (!areAllFilesVideoFiles) {
          errors[FieldName.files] = t("machineTranslation.mediaTranslation.validation.allFilesMustBeVideo");
        }
      }

      // Validate the dubbing request by checking that selected target languages support dubbing
      if (mediaProjectType === MediaProjectType.DUBBING && xl8DubbingVoices) {
        const voiceLanguageLltsIds = Array.from(dubbingVoicesByTargetLanguage.entries()).map(
          ([language]) => language.id
        );
        const languagesNotSupportingDubbing = values[FieldName.targetLanguages]
          .filter(
            targetLangOption =>
              !voiceLanguageLltsIds.includes(targetLangOption.value) && targetLangOption.value !== sourceLanguageId
          )
          .map(l => l.label);
        if (languagesNotSupportingDubbing.length > 0) {
          errors[FieldName.targetLanguages] = t(
            "machineTranslation.mediaTranslation.validation.dubbingNotSupportedLanguages",
            { languages: languagesNotSupportingDubbing.join(", ") }
          );
        }
      }
      return errors;
    },
    [
      dubbingVoicesByTargetLanguage,
      mediaProjectType,
      mtSettings?.maxAudioFileSizeMB,
      mtSettings?.maxVideoFileSizeMB,
      t,
      xl8DubbingVoices
    ]
  );

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      resetS3FileUpload();
      const files = values[FieldName.files];
      await uploadFiles({
        files,
        onSuccess: async fileKeys => {
          const jobRequestFiles: JobRequestNewFile[] = files.map((file, index) => ({
            fileName: file.name,
            category: JobRequestFileCategory.SOURCE_DOCUMENT,
            languageId: values[FieldName.sourceLanguage]?.value,
            fileKey: fileKeys[index]
          }));
          const isDubbingRequested = mediaProjectType === MediaProjectType.DUBBING;
          const dubbingVoices: DubbingLanguageAndVoice[] | undefined = !isDubbingRequested
            ? undefined
            : Object.keys(values)
                .filter(
                  key =>
                    key.startsWith(FieldName.dubbingVoicePrefix) &&
                    values[key as `${FieldName.dubbingVoicePrefix}#${string}`]
                )
                .map(key => ({
                  languageId: key.split("#")[1],
                  voiceName: values[key as `${FieldName.dubbingVoicePrefix}#${string}`]
                }));
          const isSubtitleEmbeddingRequested =
            mediaProjectType === MediaProjectType.EMBEDDING_TRANSLATED_SUBTITLES_INTO_SOURCE_VIDEO;
          await MachineTranslationService.translateMediaFiles({
            requestBody: {
              requestName: values[FieldName.projectName],
              files: jobRequestFiles,
              sourceLanguageId: values[FieldName.sourceLanguage]!.value!,
              targetLanguageIds:
                mediaProjectType === MediaProjectType.TRANSCRIPTION
                  ? [values[FieldName.sourceLanguage]!.value!]
                  : values[FieldName.targetLanguages].map(o => o.value),
              subtitlesType: values[FieldName.subtitlesType],
              isDubbingRequested,
              dubbingVoices,
              isSubtitleEmbeddingRequested
            }
          });
          onSuccess();
        }
      });
    },
    [mediaProjectType, onSuccess, resetS3FileUpload, uploadFiles]
  );

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage apiError={error} />;
  }

  // Get the project type title based on the mediaProjectType
  const getProjectTypeTitle = () => {
    switch (mediaProjectType) {
      case MediaProjectType.TRANSCRIPTION:
        return t("machineTranslation.mediaTranslation.projectTypes.transcription.title");
      case MediaProjectType.TRANSCRIPTION_AND_TRANSLATION:
        return t("machineTranslation.mediaTranslation.projectTypes.transcriptionAndTranslation.title");
      case MediaProjectType.DUBBING:
        return t("machineTranslation.mediaTranslation.projectTypes.dubbing.title");
      case MediaProjectType.EMBEDDING_TRANSLATED_SUBTITLES_INTO_SOURCE_VIDEO:
        return t("machineTranslation.mediaTranslation.projectTypes.embeddingSubtitles.title");
      default:
        return "";
    }
  };

  return (
    <>
      {filesUploadError && <SnackbarApiError error={filesUploadError} />}

      {/* Project Type Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 500 }}>
          {getProjectTypeTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("machineTranslation.mediaTranslation.projectSetupInstructions")}
        </Typography>
      </Box>

      <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit}>
        {mtSettings && (
          <CreateMediaProjectForm
            mtSettings={mtSettings}
            sourceLanguageOptions={sourceLanguageOptions}
            targetLanguageOptions={targetLanguageOptions}
            areXl8DubbingVoicesLoading={areXl8DubbingVoicesLoading}
            dubbingVoicesByTargetLanguage={dubbingVoicesByTargetLanguage}
            onSourceLanguageChange={setSelectedSourceLanguageId}
            onTargetLanguagesChange={setSelectedTargetLanguageIds}
            hideTargetLanguageSelection={mediaProjectType === MediaProjectType.TRANSCRIPTION}
            hideSubtitleTypeSelection={
              mediaProjectType === MediaProjectType.DUBBING ||
              mediaProjectType === MediaProjectType.EMBEDDING_TRANSLATED_SUBTITLES_INTO_SOURCE_VIDEO
            }
            enableVoiceSelection={mediaProjectType === MediaProjectType.DUBBING}
            onCancel={onClose}
          />
        )}
      </Formik>
      {areFilesUploading && (
        <AlertDialog title={t("machineTranslation.mediaTranslation.uploadingAlert.title")}>
          <Stack spacing={1}>
            <Box>{t("machineTranslation.mediaTranslation.uploadingAlert.message")}</Box>
            <Box sx={{ width: "100%" }}>
              <LinearProgressWithLabel value={uploadProgress} />
            </Box>
          </Stack>
        </AlertDialog>
      )}
    </>
  );
};

export { CreateMediaProjectStep };
